import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { supabase } from '../lib/supabase';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('🔐 AuthCallback: Starting authentication process...');
        
        // Get the current URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (error) {
          console.error('❌ AuthCallback: OAuth error:', error, errorDescription);
          navigate('/login?error=' + encodeURIComponent(errorDescription || 'Authentication failed'));
          return;
        }

        // Check for existing session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ AuthCallback: Session error:', sessionError);
          navigate('/login?error=' + encodeURIComponent('Session error: ' + sessionError.message));
          return;
        }

        if (session?.user) {
          console.log('✅ AuthCallback: User authenticated successfully:', session.user.email);
          
          // Set a small delay to ensure session is properly established
          setTimeout(() => {
            navigate('/dashboard');
          }, 1000);
        } else {
          console.log('⚠️ AuthCallback: No session found, checking for auth state change...');
          
          // Listen for auth state changes
          const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('🔄 AuthCallback: Auth state changed:', event, session?.user?.email);
            
            if (event === 'SIGNED_IN' && session?.user) {
              console.log('✅ AuthCallback: User signed in successfully');
              navigate('/dashboard');
            } else if (event === 'SIGNED_OUT') {
              console.log('❌ AuthCallback: User signed out');
              navigate('/login');
            }
          });

          // Cleanup subscription after 10 seconds
          setTimeout(() => {
            subscription.unsubscribe();
            if (!session?.user) {
              console.log('⏰ AuthCallback: Timeout - no session established');
              navigate('/login?error=' + encodeURIComponent('Authentication timeout'));
            }
          }, 10000);
        }
      } catch (error) {
        console.error('❌ AuthCallback: Unexpected error:', error);
        navigate('/login?error=' + encodeURIComponent('Unexpected error during authentication'));
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6">
        Completing sign-in...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we authenticate your account
      </Typography>
    </Box>
  );
};

export default AuthCallback; 